/* Feb-2026 · Genre Spotlights + New Arrivals rows for the bookstore landing.
 *
 * Two lightweight merchandising strips:
 *   1. `SpotlightsRow`   — GET /api/bookstore/spotlights (admin-curated)
 *   2. `NewArrivalsRow`  — GET /api/bookstore/new-arrivals?days=14
 *
 * Each strip renders its own state fetch; if the endpoint returns an
 * empty list the strip hides itself so the landing stays tidy.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Newspaper } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax  = axios.create({ baseURL: `${API}/api`, withCredentials: true });

function formatPrice(cents, currency = 'USD') {
  if (!cents && cents !== 0) return '';
  try {
    return new Intl.NumberFormat(undefined,
      { style: 'currency', currency }).format(cents / 100);
  } catch { return `$${(cents / 100).toFixed(2)}`; }
}

function MiniBookCard({ book, testid }) {
  return (
    <Link
      to={`/bookstore#book=${encodeURIComponent(book.id)}`}
      className="group flex w-32 shrink-0 flex-col"
      data-testid={testid}
    >
      <div className="aspect-[2/3] w-full overflow-hidden rounded-md border border-border bg-secondary shadow-sm transition group-hover:shadow-md">
        {book.cover_url
          ? <img src={book.cover_url} alt={book.title}
                 className="h-full w-full object-cover transition group-hover:scale-[1.03]"
                 loading="lazy" />
          : <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wider text-muted-foreground">
              {book.title?.slice(0, 20) || 'Untitled'}
            </div>}
      </div>
      <div className="mt-1.5 line-clamp-2 text-[12px] font-serif leading-tight">
        {book.title}
      </div>
      <div className="text-[10.5px] text-muted-foreground">
        {book.author}
      </div>
      <div className="text-[10.5px] font-mono">
        {formatPrice(book.price_cents, book.currency)}
      </div>
    </Link>
  );
}

export function SpotlightsRow() {
  const [state, setState] = useState(null);
  useEffect(() => {
    ax.get('/bookstore/spotlights')
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, []);
  if (!state || !state.spotlights?.length) return null;
  // Hide slots with zero resolved books so early-launch periods don't
  // show empty rails.
  const populated = state.spotlights.filter((s) => s.resolved_books?.length);
  if (!populated.length) return null;
  return (
    <section
      className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24"
      data-testid="bs-spotlights-section"
    >
      <header className="mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="font-serif text-[18px]">Genre spotlights</h2>
        <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          week of {state.week_of}
        </span>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3"
           data-testid="bs-spotlights-grid">
        {populated.map((s) => (
          <div key={s.genre_slug}
               className="rounded-md border border-border bg-card p-3"
               data-testid={`bs-spotlight-${s.genre_slug}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <h3 className="font-serif text-[15px]">{s.headline}</h3>
                <Link to={`/bookstore/g/${s.genre_slug}`}
                      className="text-[10.5px] uppercase tracking-wider text-muted-foreground hover:text-primary">
                  Shop all →
                </Link>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1"
                 data-testid={`bs-spotlight-books-${s.genre_slug}`}>
              {s.resolved_books.slice(0, 4).map((b) => (
                <MiniBookCard key={b.id} book={b}
                              testid={`bs-spotlight-book-${b.id}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function NewArrivalsRow() {
  const [books, setBooks] = useState(null);
  useEffect(() => {
    ax.get('/bookstore/new-arrivals?days=14&limit=10')
      .then((r) => setBooks(r.data?.books || []))
      .catch(() => setBooks([]));
  }, []);
  if (!books) return null;
  if (books.length === 0) return null;
  return (
    <section
      className="relative z-10 mx-auto max-w-6xl px-6 pt-8 lg:pl-24"
      data-testid="bs-new-arrivals-section"
    >
      <header className="mb-3 flex items-center gap-2">
        <Newspaper className="h-4 w-4 text-primary" />
        <h2 className="font-serif text-[18px]">Fresh off the Loom</h2>
        <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          last 14 days
        </span>
      </header>
      <div className="flex gap-4 overflow-x-auto pb-2"
           data-testid="bs-new-arrivals-strip">
        {books.map((b) => (
          <MiniBookCard key={b.id} book={b}
                        testid={`bs-new-arrival-${b.id}`} />
        ))}
      </div>
    </section>
  );
}
